import { BasePlay, BasketballCourtView, FrameStencil, HockeyRinkView, SportPlay } from "../../../../types";
import { SoccerPitchView } from "../../../../types/play/soccer";
import { Series } from "../../series-api";
import { Sport } from "../../sport-slice";
import { Team } from "../../team-api";
import { BasketballDrawingArea, HockeyDrawingArea, PlayPayload, Stencil } from "../types";
import { SoccerDrawingArea } from "../types/play-payload-soccer";
import { convertBBDBStencilToFrameStencil } from "./convert-bb-db-stencil-to-frame-stencil";

const parseJson = <TResponse = any>(value: string): TResponse => {
  try {
    return JSON.parse(value);
  } catch (err) {
    return [] as TResponse;
  }
};

const validateD3Stencils = (value: string): FrameStencil[] => {
  const stencils = parseJson<unknown[]>(value);

  return stencils.filter((stencil): stencil is FrameStencil => {
    return !(stencil as Stencil)?.stencilName;
  });
};

interface ObjOfBooleanValues {
  [key: string]: boolean;
}

const convertStringToObjOfBooleanValues = (value: string): ObjOfBooleanValues => {
  try {
    const options: { [key: string]: unknown } = JSON.parse(value);

    const optionsValidated: ObjOfBooleanValues = Object.entries(options).reduce(
      (acc: ObjOfBooleanValues, [key, value]): ObjOfBooleanValues => {
        if (typeof value === "boolean") return { ...acc, [key]: value };
        else return acc;
      },
      {}
    );

    return optionsValidated;
  } catch (err) {
    return {};
  }
};

const parseOrientation = (value: string | number | undefined): 0 | 90 | 180 | 270 => {
  if (typeof value === "number") {
    if (value === 0 || value === 90 || value === 180 || value === 270) return value;
  }

  if (typeof value === "string") {
    const parsedValue = parseInt(value);

    if (parsedValue === 0 || parsedValue === 90 || parsedValue === 180 || parsedValue === 270) return parsedValue;
  }

  return 0;
};

const parseSoccerOutOfBounds = (value: boolean | string | undefined): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value === "true";
  return false;
};

const hockeyDrawingAreaToRinkViewMap = new Map<HockeyDrawingArea, HockeyRinkView>([
  [HockeyDrawingArea.FULL, HockeyRinkView.FULL],
  [HockeyDrawingArea.LEFT_RINK, HockeyRinkView.LEFT_RINK],
  [HockeyDrawingArea.RIGHT_RINK, HockeyRinkView.RIGHT_RINK],
  [HockeyDrawingArea.GOAL, HockeyRinkView.GOAL],
  [HockeyDrawingArea.LEFT_ONE_THIRD, HockeyRinkView.LEFT_ONE_THIRD],
  [HockeyDrawingArea.RIGHT_ONE_THIRD, HockeyRinkView.RIGHT_ONE_THIRD],
  [HockeyDrawingArea.LEFT_TWO_THIRD, HockeyRinkView.LEFT_TWO_THIRD],
  [HockeyDrawingArea.RIGHT_TWO_THIRD, HockeyRinkView.RIGHT_TWO_THIRD],
]);

const drawingAreaToBasketballCourtViewMap = new Map<BasketballDrawingArea, BasketballCourtView>([
  [BasketballDrawingArea.FRONTCOURT, BasketballCourtView.FRONTCOURT],
  [BasketballDrawingArea.BACKCOURT, BasketballCourtView.BACKCOURT],
  [BasketballDrawingArea.FULLCOURT, BasketballCourtView.FULLCOURT],
]);

const soccerDrawingAreaToPitchViewMap = new Map<SoccerDrawingArea, SoccerPitchView>([
  [SoccerDrawingArea.FULL, SoccerPitchView.FULL],
  [SoccerDrawingArea.LEFT_HALF, SoccerPitchView.LEFT_HALF],
  [SoccerDrawingArea.RIGHT_HALF, SoccerPitchView.RIGHT_HALF],
  [SoccerDrawingArea.LEFT_QUARTER, SoccerPitchView.LEFT_QUARTER],
  [SoccerDrawingArea.RIGHT_QUARTER, SoccerPitchView.RIGHT_QUARTER],
]);

export interface ConvertPlayPayloadToSportPlayProps {
  play: PlayPayload;
  teams: Team[];
  series: Series[];
}

export const convertPlayPayloadToSportPlay = ({
  play,
  teams,
  series,
}: ConvertPlayPayloadToSportPlayProps): SportPlay => {
  const {
    sport,
    id,
    drawId,
    name,
    teamId,
    seriesId,
    tags,
    notes,
    framesDTO,
    courtType,
    createdTimestamp,
    updatedTimestamp,
    properties,
    assetsDTO,
    recipientsDTO,
    seasonRaw,
  } = play;

  const courtOptions = convertStringToObjOfBooleanValues(properties?.playOptions || "{}");

  const basePlay: Omit<BasePlay, "sport" | "playSurfaceView" | "frames" | "properties"> = {
    id,
    drawId,
    name,
    team: teams?.find((team) => team.team_id === teamId),
    series: series?.find((series) => series.series_id === seriesId),
    season: seasonRaw,
    tags: tags || [],
    notes,
    playSurfaceId: courtType,
    createdTimestamp,
    updatedTimestamp,
    courtOptions,
    assets: assetsDTO || [],
    recipients: recipientsDTO,
    orientation: parseOrientation(properties?.orientation),
  };

  switch (sport) {
    case Sport.BASKETBALL: {
      const properties = play?.properties;
      const outOfBounds = convertStringToObjOfBooleanValues(properties?.outOfBounds || "{}");

      return {
        ...basePlay,
        sport,
        frames:
          framesDTO?.map((frameDTO) => ({
            id: frameDTO.id,
            frameStencils: convertBBDBStencilToFrameStencil(frameDTO) || [],
            label: frameDTO.label,
            description: frameDTO.description || "",
            // keep existing bezierStencils so that old frames on mobile are not blank when saved with ?done=false
            bezierStencils: frameDTO.bezierStencils,
          })) || [],
        playSurfaceView:
          drawingAreaToBasketballCourtViewMap.get(play.drawingArea || BasketballDrawingArea.FRONTCOURT) ||
          BasketballCourtView.FRONTCOURT,
        properties: {
          outOfBoundsEnd: outOfBounds.outOfBoundsEnd ? outOfBounds.outOfBoundsEnd : false,
          outOfBoundsLeft: outOfBounds.outOfBoundsLeft ? outOfBounds.outOfBoundsLeft : false,
          outOfBoundsRight: outOfBounds.outOfBoundsRight ? outOfBounds.outOfBoundsRight : false,
        },
      };
    }

    case Sport.HOCKEY: {
      const playSurfaceView =
        hockeyDrawingAreaToRinkViewMap.get(play.drawingArea || HockeyDrawingArea.FULL) || HockeyRinkView.FULL;
      return {
        ...basePlay,
        frames:
          framesDTO?.map((frameDTO) => ({
            ...frameDTO,
            description: frameDTO.description || "",
            frameStencils: validateD3Stencils(frameDTO?.d3Stencils || "[]"),
          })) || [],
        sport,
        playSurfaceView,
        properties: {},
      };
    }

    case Sport.SOCCER: {
      const playSurfaceView =
        soccerDrawingAreaToPitchViewMap.get(play.drawingArea || SoccerDrawingArea.FULL) || SoccerPitchView.FULL;

      return {
        ...basePlay,
        frames:
          framesDTO?.map((frame) => ({
            ...frame,
            description: frame.description || "",
            frameStencils: validateD3Stencils(frame?.d3Stencils || "[]"),
          })) || [],
        sport,
        playSurfaceView,
        properties: {},
        outOfBounds: parseSoccerOutOfBounds(play.properties?.outOfBounds),
      };
    }
  }
};
